import { EMPTY_ARRAY, EMPTY_OBJECT } from 'constants/defaults';
import { errorMessageFactory } from 'factories/api';
import { intersection } from 'lodash-es';
import type { TagNormalized, TagsWithChildrenDictionary } from 'redux/modules/tags.types';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import { getProjectVariantMap } from 'redux/selectors/projects';
import { getUserPermissions } from 'redux/selectors/user';
import { getFullProjectOrNull } from 'redux/selectors/utils/projects';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import { TagSettings } from 'types/project';
import type { Tag } from 'types/tag';
import { naturalCompare } from 'utils/naturalSort';
import { enrichTicMeta } from 'utils/tic/enrichTicMeta';

const readTags = (state: IState) => state.tags.entities.tag;
const readTagResult = (state: IState) => state.tags.result;
const readShowEditor = (state: IState) => state.tags.showEditor;
const readDraft = (state: IState) => state.tags.draft;
const readTagError = (state: IState) => state.tags.error;
const readXhrStatus = (state: IState) => state.tags.xhrStatus;

export const getIsManagingTags = readShowEditor;
const getDraft = readDraft;

export const getXhrStatus = createSelector([readXhrStatus], xhrStatus =>
  xhrStatusUIState(xhrStatus)
);

export const getTagEntities = readTags;

export const getTagsWithChildren = createSelector([readTags], tags => {
  if (tags == null) {
    return EMPTY_OBJECT;
  }

  return Object.keys(tags).reduce((acc: TagsWithChildrenDictionary, tagIdString) => {
    const tagId = +tagIdString;
    const originalTag = tags[tagId];
    const tag: Tag = { ...tags[tagId], children: undefined };

    if (originalTag.children && originalTag.children.length) {
      tag.children = originalTag.children.map(id => {
        return {
          ...tags[id],
          // Needed to make TS happy. tags[id].children will always be undefined anyway since it's a subtag
          children: undefined,
        };
      });
    }

    acc[tagId] = tag;
    return acc;
  }, {});
});

export const getTags = createSelector([readTags, readTagResult], (tags, tagResult) => {
  return tagResult.map(tagId => {
    const tag: TagNormalized = tags[tagId];
    const { children, ...rest } = tag;

    const fullTag: Tag = {
      ...rest,
      children: tag.children
        ? tag.children.map(childId => {
            const { children, ...childRest } = tags[childId];
            return enrichTicMeta({ ...childRest });
          })
        : [],
    };
    return enrichTicMeta(fullTag);
  });
});

export const getSortedTags = createSelector([getTags], tags => {
  return tags
    .sort((a, b) => naturalCompare(a.name, b.name))
    .map(tag =>
      !tag.children
        ? tag
        : {
            ...tag,
            children: tag.children.sort((a, b) => naturalCompare(a.sequence, b.sequence)),
          }
    );
});

export const getTagsWithDraft = createSelector([getDraft, getSortedTags], (draft, tags) => {
  if (!draft) {
    return tags;
  }

  if (!draft.parent_id) {
    return [draft, ...tags];
  }

  return tags.map(tag =>
    draft.parent_id === tag.id ? { ...tag, children: [draft, ...(tag.children || [])] } : tag
  );
});

export const getError = createSelector([readTagError], error => {
  return error ? errorMessageFactory(error) : error;
});

export const makeGetTagXhrStatusStateByKey = () =>
  createSelector(
    [readXhrStatus, (_: unknown, props: { key?: string }) => props.key],
    (xhrStatus, key) => xhrStatusUIState(xhrStatus, key)
  );

export const getTagsEditor = createSelector(
  [getIsManagingTags, getTagsWithDraft, getDraft, getError, getUserPermissions, getXhrStatus],
  (showEditor, tags, draft, error, permissions, xhrStatus) => {
    return {
      tags,
      draft,
      permissions,
      xhrStatus,
      ui: {
        showEditor,
        error,
      },
    };
  }
);

export const getTagSearchProps = createSelector(
  [(_: unknown, props: { forProjectId?: number }) => props],
  props => props
);

export const makeGetTagSearchOptions = () =>
  createSelector(
    [getTags, readTags, readTagResult, getTagSearchProps, getProjectVariantMap],
    (tags, tagEntities, tagResult, componentProps, projectVariantMap) => {
      const projectId = componentProps.forProjectId || null;
      const emptyTags: Array<Tag> = [];

      if (!projectId) {
        return emptyTags;
      }

      const lookupProject = getFullProjectOrNull(projectVariantMap[projectId]);

      if (!lookupProject) {
        return emptyTags;
      }

      const projectLabelSetting = lookupProject.enable_labels;
      if (projectLabelSetting === TagSettings.All) {
        return tags;
      }
      if (projectLabelSetting === TagSettings.None) {
        return emptyTags;
      }
      if (projectLabelSetting === TagSettings.Custom) {
        const lookupParents = intersection(lookupProject.label_ids, tagResult);
        return lookupParents.map(tagId => {
          const tag: TagNormalized = tagEntities[tagId];
          const { children, ...rest } = tag;

          const fullTag: Tag = {
            ...rest,
            children: tag.children
              ? tag.children.map(childId => {
                  const { children, ...childRest } = tagEntities[childId];
                  return { ...childRest };
                })
              : [],
          };
          return fullTag;
        });
      }

      return tags;
    }
  );

// Memoized selector for getting tag search options by project ID
export const getTagSearchOptionsByProjectId = createSelector(
  [
    getTags,
    readTags,
    readTagResult,
    getProjectVariantMap,
    (_: IState, projectId: number | null) => projectId,
  ],
  (tags, tagEntities, tagResult, projectVariantMap, projectId) => {
    const emptyTags: Array<Tag> = [];

    if (!projectId) {
      return emptyTags;
    }

    const lookupProject = getFullProjectOrNull(projectVariantMap[projectId]);

    if (!lookupProject) {
      return emptyTags;
    }

    const projectLabelSetting = lookupProject.enable_labels;
    if (projectLabelSetting === TagSettings.All) {
      return tags;
    }
    if (projectLabelSetting === TagSettings.None) {
      return emptyTags;
    }
    if (projectLabelSetting === TagSettings.Custom) {
      const lookupParents = intersection(lookupProject.label_ids, tagResult);
      return lookupParents.map(tagId => {
        const tag: TagNormalized = tagEntities[tagId];
        const { children, ...rest } = tag;

        const fullTag: Tag = {
          ...rest,
          children: tag.children
            ? tag.children.map(childId => {
                const { children, ...childRest } = tagEntities[childId];
                return { ...childRest };
              })
            : [],
        };
        return fullTag;
      });
    }

    return tags;
  }
);

function flattenTags(tags: Array<Tag>): Array<Tag> {
  return tags.reduce((acc: Array<Tag>, tag: Tag) => {
    acc.push(tag);
    if (tag.children) {
      acc.push(...flattenTags(tag.children));
    }
    return acc;
  }, []);
}

export const getTagsForProjectFilter = createSelector([getSortedTags], tags => {
  if (!tags.length) {
    return EMPTY_ARRAY;
  }

  return flattenTags(tags);
});

// A multi-project edit can only assign tag lists enabled for every project.
export const makeGetTagSearchOptionsForProjects = (projectIds: (number | null)[]) =>
  createSelector([getTags, getProjectVariantMap], (tags, projects) =>
    tags.filter(
      tag =>
        projectIds.length > 0 &&
        projectIds.every(id => {
          const project = id == null ? null : getFullProjectOrNull(projects[id]);
          if (!project || project.enable_labels === TagSettings.None) return false;
          return project.enable_labels !== TagSettings.Custom || project.label_ids.includes(tag.id);
        })
    )
  );
